import useWebSocket from "react-use-websocket";
import { useAuth } from "react-oidc-context";
import React, { useEffect, useRef, useState } from "react";
import {
  ManagerContext,
  unknown,
  type ManagerState,
  type ManagerStatus,
} from "./utilities/manager";
import useApi from "./Api";

type IdentifyEvent = {
  event: "identify";
  connectionId: string;
};

type UploadedModJarEvent = {
  event: "uploaded-mod-jar";
  filename: string;
};

type RecordedModIdEvent = {
  event: "recorded-mod-id";
  modId: string;
  version: string;
  displayName: string;
  description: string;
};

type ModImportFinishedEvent = {
  event: "mod-import-finished";
};

type ManagerStatusEvent = {
  event: "manager-status";
  state: ManagerState;
  worldUuid: string | null;
};

type IdentifyRequest = {
  action: "identify";
};

type ConsoleSocketRequest = IdentifyRequest;

type ConsoleSocketEvent =
  | IdentifyEvent
  | UploadedModJarEvent
  | RecordedModIdEvent
  | ModImportFinishedEvent
  | ManagerStatusEvent;

const SocketProvider: React.FunctionComponent<React.PropsWithChildren> = ({
  children,
}) => {
  const auth = useAuth();
  const [connectionId, setConnectionId] = useState<string | null>(null);
  const [managerStatus, setManagerStatus] = useState<ManagerStatus>(unknown());
  const api = useApi();

  async function pingManager() {
    console.log("PING");
    await api.post("manager/ping");
  }

  async function registerSocket(connectionId: string) {
    const previousConnectionId = localStorage.getItem("socketConnectionId");
    if (previousConnectionId) {
      await api.delete(`sockets/${previousConnectionId}`);
      localStorage.removeItem("socketConnectionId");
    }
    await api.put(`sockets/${connectionId}`);
    localStorage.setItem("socketConnectionId", connectionId);
    await pingManager();
  }

  useEffect(() => {
    if (connectionId !== null) {
      registerSocket(connectionId);
    }
  }, [connectionId]);

  function forgetManager() {
    setManagerStatus(unknown());
  }

  const { lastJsonMessage, sendJsonMessage, readyState } =
    useWebSocket<ConsoleSocketEvent>(
      "wss://cn8k9m1a65.execute-api.eu-north-1.amazonaws.com/production/",
      {
        onOpen: () => {
          console.log("Socket connection opened");
          sendJsonMessage<ConsoleSocketRequest>({
            action: "identify",
          });
        },
        onClose: () => {
          console.log("Socket connection closed");
        },
      }
    );

  useEffect(() => {
    if (lastJsonMessage) {
      console.log("Handling socket message: ", lastJsonMessage);

      if (lastJsonMessage) {
        switch (lastJsonMessage.event) {
          case "identify":
            setConnectionId(lastJsonMessage.connectionId);
            break;

          case "manager-status":
            const { state, worldUuid } = lastJsonMessage;
            setManagerStatus({
              state,
              worldUuid,
              ping: pingManager,
              forget: forgetManager,
            });
        }
      }
    }
  }, [lastJsonMessage]);

  return (
    <ManagerContext.Provider value={managerStatus}>
      {children}
    </ManagerContext.Provider>
  );
};

export default SocketProvider;
